import React, { createContext, useEffect, useState } from "react"
import { useSelector } from "react-redux"
import {
  applyFeatureFlagOverrides,
  checkFeatureFlags
} from "../../mp-utils/generic-utils"
import { User } from "../../mp-database"
import { RootState } from "../redux/store"
import { StartupSettings, FeatureFlagContextType } from "./types"
import { defaultSettings } from "./default"
import { FeatureFlagStatesType } from "../../mp-utils/generic-utils/feature-flags/types"
import Loader from "../../mp-utils/ready-utils/components/Loader"
import { GET_ENUMERATION } from "../../reporting/graphql/queries/enumerationQueries"
import { executeQuerySideload } from "../../../services/generic-services/helpers/studyPlan/studyPlanExecutors"
import { useRetry } from '../../mp-core/helpers/useRetry'

export const FeatureFlagContext = createContext<FeatureFlagContextType>({
  featureFlags: {},
  featureFlagsLoading: false,
  startupSettings: {}
})

type Props = {}

export const FeatureFlagProvider: React.FC<Props> = ({ children }) => {
  const user = useSelector((state: RootState) => state.users.testUser as User)

  const [enabled, setEnabled] = useState<FeatureFlagStatesType>({})
  const [loading, setLoading] = useState<boolean>(true)
  const [settings, setSettings] = useState<StartupSettings>(defaultSettings)
  const { execute: executeFF } = useRetry(async () => {
    return checkFeatureFlags({
      userId: user?.email
    })
  })
  const { execute: executeSettings } = useRetry(async () => {
    return (await executeQuerySideload(GET_ENUMERATION, {
      type: 'CONFIG',
      id: 'startup'
    }))?.enumerations?.getEnumeration?.enumeration?.settings
  })

  useEffect(() => {
    let isMounted = true

    const setupFeatureFlagsState = async () => {
      setLoading(true)

      try {

        const [featureFlagsState, enumSettings] = await Promise.all([executeFF(), executeSettings()])

        if (isMounted) {
          setEnabled(applyFeatureFlagOverrides(featureFlagsState))
          setSettings({
            ...defaultSettings,
            ...enumSettings,
          })
        }
      } catch (error) {
        console.warn("[FeatureFlagProvider]", error)
      } finally {
        setLoading(false)
      }
    }

    setupFeatureFlagsState()

    return () => {
      isMounted = false
    }
  }, [user])

  return (
    <FeatureFlagContext.Provider
      value={{
        featureFlags: enabled,
        featureFlagsLoading: loading,
        startupSettings: settings,
      }}
    >
      {loading?<Loader />:children}
    </FeatureFlagContext.Provider>
  )
}
